/**
 * 사이트 데이터 변환 서비스
 * ISitePublic, ISiteTableItem, ISiteFormData 간의 변환을 담당
 */

import {
  ISitePublic,
  SitePublicFactory,
  ISiteTableItem,
  SiteTableItemFactory,
  ISiteFormData,
  SiteFormDataFactory
} from '../models';

/**
 * 사이트 데이터 변환 서비스
 */
export class SiteDataTransformService {
  /**
   * SitePublic을 SiteTableItem으로 변환
   */
  static toTableItem(sitePublic: ISitePublic): ISiteTableItem {
    return SiteTableItemFactory.fromSitePublic(sitePublic);
  }

  /**
   * SitePublic 배열을 SiteTableItem 배열로 변환
   */
  static toTableItemArray(sitePublicArray: ISitePublic[]): ISiteTableItem[] {
    return SiteTableItemFactory.fromSitePublicArray(sitePublicArray);
  }

  /**
   * SitePublic을 SiteFormData로 변환
   */
  static toFormData(sitePublic: ISitePublic): ISiteFormData {
    return SiteFormDataFactory.fromSitePublic(sitePublic);
  }

  /**
   * SiteFormData를 SitePublic로 변환
   */
  static toSitePublic(formData: ISiteFormData): ISitePublic | null {
    return SiteFormDataFactory.toSitePublic(formData);
  }

  /**
   * 업로드용 FormData 객체 생성
   */
  static toFormDataForUpload(data: any): FormData {
    const formData = new FormData();

    // SitePublic 기본 필드들 (백엔드 구조 기준) - 옵셔널 체이닝 적용
    data.name && formData.append('name', data.name);
    data.description && formData.append('description', data.description);
    data.address && formData.append('address', data.address);
    data.establishedDate && formData.append('establishedDate', data.establishedDate);
    data.powerCapacity && formData.append('powerCapacity', data.powerCapacity.toString());
    data.pvModuleMaker && formData.append('pvModuleMaker', data.pvModuleMaker);
    data.pvModuleCount && formData.append('pvModuleCount', data.pvModuleCount.toString());
    data.pvModuleOutput && formData.append('pvModuleOutput', data.pvModuleOutput.toString());
    data.rotation != null && formData.append('rotation', data.rotation.toString());

    // panelProductSeq (SitePublic 구조)
    data.panelProduct?.value && formData.append('panelProductSeq', data.panelProduct.value);

    // project 정보 (SitePublic.project: Project 객체)
    data.project?.id && formData.append('project.seq', data.project.id);

    // 사이트 좌표 정보 (SitePublic.siteCoords: Coordinates)
    data?.location?.coordinates?.[0] &&
      formData.append('siteCoords.lon', data.location.coordinates[0]);
    data?.location?.coordinates?.[1] &&
      formData.append('siteCoords.lat', data.location.coordinates[1]);
    data?.location?.crs?.properties?.name &&
      formData.append('siteCoords.crsName', data.location.crs.properties.name);

    // 회사 정보 (SitePublic.ownerCompany: CompanyPublic)
    data.ownerCompany?.id && formData.append('ownerCompany.seq', data.ownerCompany.id);
    data.operationCompany?.id && formData.append('operationCompany.seq', data.operationCompany.id);

    // 담당자 정보 (SitePublic.managers: List<CompanyManagerPublic>)
    // selectedManagers에서 managers[index].seq 형태로 전송
    data.selectedManagers?.forEach((manager: any, index: number) => {
      const managerSeq = manager.id ?? manager.seq;
      managerSeq && formData.append(`managers[${index}].seq`, managerSeq);
      manager.responsibility &&
        formData.append(`managers[${index}].responsibility`, manager.responsibility);
    });

    // 파일 업로드
    data.siteImageFile && formData.append('siteImageFile', data.siteImageFile);

    return formData;
  }

  /**
   * API 응답에서 SitePublic 생성
   */
  static fromApiResponse(apiResponse: any): ISitePublic {
    return SitePublicFactory.fromApiResponse(apiResponse);
  }

  /**
   * API 응답 배열에서 SitePublic 배열 생성
   */
  static fromApiResponseArray(apiResponseArray: any[]): ISitePublic[] {
    return SitePublicFactory.fromApiResponseArray(apiResponseArray);
  }

  /**
   * 폼 데이터 유효성 검사
   */
  static validateFormData(formData: ISiteFormData): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!formData.name?.trim()) {
      errors.push('사이트명은 필수입니다.');
    }

    if (!formData.project?.seq || formData.project.seq <= 0) {
      errors.push('프로젝트는 필수 선택입니다.');
    }

    if (formData.powerCapacity <= 0) {
      errors.push('발전용량은 0보다 커야 합니다.');
    }

    if (formData.pvModuleCount <= 0) {
      errors.push('PV 모듈 개수는 0보다 커야 합니다.');
    }

    if (formData.pvModuleOutput <= 0) {
      errors.push('PV 모듈 출력은 0보다 커야 합니다.');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * 빈 폼 데이터 생성
   */
  static createEmptyFormData(): ISiteFormData {
    return SiteFormDataFactory.createEmpty();
  }

  /**
   * 빈 테이블 아이템 생성
   */
  static createEmptyTableItem(): ISiteTableItem {
    return SiteTableItemFactory.createEmpty();
  }
}

/**
 * 기본 export
 */
export default SiteDataTransformService;
